import Ext from "Ext"
import $ from "jquery"
import SelectorPanel from "./at_selector_panel"
import ShowDisabledCheckbox from "./ShowDisabledCheckbox"
import IconButton from "../at_icon_button"
import messageResource from "../../messageResource"
import FlatButton from "../at_flat_button"
import GradientButton from "../at_gradient_button"
import {atImgPath} from "../../atBrowserUtils"
import {truncateStringByLength} from "../../atUtils"

/**
 * Abstract selector panel with basic set of components.
 *
 * Depends on:
 * <code>/js/controls/selectorControls/at_selector_panel.js</code>
 * <code>/js/controls/selectorControls/selectionHelpers/at_selection_helper.js</code>
 * <code>/js/controls/at_flat_button.js</code>
 * <code>/js/message_resources.js</code> with prefix "common"
 *
 * Defaults styles in:
 * <code>/css/extjs/at-css/at-selector-panel.less</code>
 *
 * @cfg {Array} data data structure with selection items for the selector tree (required).
 * @cfg {String} optionValue selected option's value (required).
 * @cfg {Boolean} showInactive initial value of "show inactive" checkbox.
 */
const GenericSelectorPanel = Ext.extend(SelectorPanel, {
    data: [],
    optionsName: "allUsersOpt",

    /**
     * @cfg {String/Number/Boolean} allItemsOptionValue value of the "all items" options.
     */
    allItemsOptionValue: "all",

    /**
     * @cfg {String/Number/Boolean} customOptionValue value of the custom option.
     */
    customOptionValue: "custom",

    /**
     * @cfg {Boolean} true to show, false to hide.
     */
    showResetButton: true,

    /**
     * @cfg {Boolean} whether to show 'Show disabled' checkbox. Note the double 'show' in the name.
     */
    showShowInactiveCheckbox: true,

    /**
     * @cfg {String} resetButtonLabelKey "Reset" button label message key.
     */
    resetButtonLabelKey: "common.reset_filter",

    /**
     * @cfg {String} closeButtonLabelKey "Close" button label message key.
     */
    closeButtonLabelKey: "common.close",

    /**
     * @cfg {String} allUsersOptionLabelKey "All users" option label message key.
     */
    allItemsOptionLabelKey: "",

    /**
     * @cfg {Array} allItemsOptionMessageParams all items option params for message from message resource.
     */
    allItemsOptionMessageParams: [],

    /**
     * @cfg {String} customOptionLabelKey "Custom" option label message key.
     */
    customOptionLabelKey: "",

    /**
     * @cfg {String} showInactiveLabelKey show inactive checkbox label message key.
     */
    showInactiveLabelKey: "",

    /**
     * @cfg {String} labelMaxChars Max label length in characters.
     */
    labelMaxChars: 50,

    /**
     * @cfg {String} labelItemNounSingularKey label item noun singular form message key.
     */
    labelItemNounSingularKey: "",

    /**
     * @cfg {String} labelItemNounPluralKey label item noun plural form message key.
     */
    labelItemNounPluralKey: "",

    /**
     * @cfg {String} labelNoItemsSelectedKey label text no items selected message key.
     */
    labelNoItemsSelectedKey: "",

    /**
     * @cfg {Boolean} labelUseNameForSingleItem true to display item name for single item.
     */
    labelUseNameForSingleItem: true,

    /**
     * @cfg {Function} selectorTreeCtor constructor for a {@link AT.SelectorTree} implementation.
     */
    selectorTreeCtor: null,

    /**
     * @cfg {Array} treeItemTooltipMessageParams specifies message to be show for selectorTree item's tooltips on some
     *              specific conditions.
     */
    treeItemTooltipMessageParams: null,

    /**
     * @cfg {Boolean} is allowed to set 'All selected' if selection is empty (potentially on init and on panel hide)
     */
    isMarkAllSelectedIsEmpty: false,

    /**
     * @cfg {Boolean} determines if panel resets when hidden (unless closeButton is clicked)
     */
    resetOnHide: true,

    /**
     * @cfg {Boolean} determine if there is an obligation to set 'All selected' on initialization if current selection is empty.
     *                Requires 'isMarkAllSelectedIsEmpty' to be true to have an effect.
     */
    isMarkAllSelectedIfEmptyOnInit: true,

    initComponent: function() {
        GenericSelectorPanel.superclass.initComponent.call(this);
        this.optionValueInit = this.optionValue;
        this.fireSelectionChangeAllowed = false;

        if (this. isMarkAllSelectedIfEmptyOnInit) {
            this.markAllSelectedIfNeeded();
        }
    },

    makeSelectionHelper: function() {
        //abstract
    },

    getSelectionHelper: function() {
        if (!this.selectionHelper) {
            this.makeSelectionHelper();
        }
        return this.selectionHelper;
    },

    /**
     * @see AT.SelectorPanel
     */
    createComponents: function() {

        this.realOptionsName = this.optionsName + "_" + this.id;
        this.createGenericOptions();

        this.createCustomOption();

        this.makeSelectionTree();

        this.selectAllButton = new IconButton({
            icon: atImgPath("selector_panel/toolbar-button-icons-sprite.png"),
            cls: "doubleButton selectAllButton"
        });
        this.clearButton = new IconButton({
            cls: "doubleButton deselectAll"
        });

        this.createFooterButtons();
        this.bindFooterButtonsEvents();
        this.createSelectionFilters();
    },

    makeSelectionTree: function() {
        this.createSelectorTree();
    },

    createFooterButtons: function() {
        this.resetButton = new FlatButton({
            icon: atImgPath("undo.gif"),
            text: messageResource.getMessage(this.resetButtonLabelKey)
        });
        this.resetButton.addClass("resetButton");
        this.closeButton = new GradientButton({
                                                  text: messageResource.getMessage(this.closeButtonLabelKey),
                                                  cls: "greyButton close-button"
                                              });
        this.cancelButton = null;
    },

    bindFooterButtonsEvents: function() {
        if (this.resetButton)
            this.resetButton.on("click", this.onResetButtonClick, this);
        if (this.closeButton)
            this.closeButton.on("click", this.onCloseButtonClick, this);
        if (this.cancelButton)
            this.cancelButton.on("click", this.onCancelButtonClick, this)
    },

    onResetButtonClick: function () {
        this.beforeResetAction();
        this.resetState(true);
        this.afterResetAction();
    },

    onCloseButtonClick: function () {
        this.shouldSave = true;
        this.fireEvent("close");
    },

    onCancelButtonClick: function () {
        this.resetState(true);
        this.afterResetAction();
        this.fireEvent("close");
    },

    beforeResetAction: function() {
        //do nothing
    },

    afterResetAction: function() {
        //do nothing
    },

    createGenericOptions: function()
    {
        this.allSelectedOption = new Ext.form.Radio({
            boxLabel: this.getAllSelectedMessage(),
            name: this.realOptionsName,
            inputValue: this.allItemsOptionValue
        })

        this.genericOptions = [
            this.allSelectedOption
        ];
    },

    updateAllSelectedLabel: function() {
        this.allSelectedOption.boxLabel = this.getAllSelectedMessage()
    },

    getAllSelectedMessage: function()
    {
        return  messageResource.getMessageWithEscapedMacros(this.getAllItemsOptionLabelKey(), this.allItemsOptionMessageParams);
    },

    getAllItemsOptionLabelKey: function()
    {
        return this.allItemsOptionLabelKey;
    },

    getCustomOptionLabelKey: function()
    {
        return this.customOptionLabelKey;
    },

    createCustomOption: function ()
    {
        this.customOption = new Ext.form.Radio({
            boxLabel: messageResource.getMessageWithEscapedMacros(this.getCustomOptionLabelKey()),
            name: this.realOptionsName,
            inputValue: this.customOptionValue
        });
    },

    createSelectorTree: function()
    {
        this.selectorTree = new this.selectorTreeCtor({
            propertiesController: this.propertiesController,
            data: this.data
        });
    },

    createSelectionFilters: function()
    {
        var panel = this;
        this.showInactiveCheckboxContainer = new Ext.Container({
            autoEl: "div",
            afterRender: function()
            {
                if (panel.showShowInactiveCheckbox)
                    panel.showInactiveCheckbox.init($(this.el.dom));
            }
        });
        this.showInactiveCheckbox = new ShowDisabledCheckbox(this.showInactiveLabelKey);
    },

    setSelectionChangedHandler: function(selectionChangedHandler) {
        this.selectionChangedHandler = selectionChangedHandler;
    },

    /**
     * @see AT.SelectorPanel
     */
    getGenericOptionsSection: function() {
        return this.genericOptions;
    },

    /**
     * @see AT.SelectorPanel
     */
    getCustomOptionSection: function() {
        return [ this.clearButton, this.makeVerticalSeparator(), this.selectAllButton,
                 this.customOption ];
    },

    makeVerticalSeparator: function() {
        return new Ext.BoxComponent({
            autoEl: {
                tag: 'div',
                cls: 'vertical-separator'
            }
        });
    },

    /**
     * @see AT.SelectorPanel
     */
    getDetailsSection: function() {
        var section = [this.selectorTree];
        if (this.showInactiveCheckboxContainer)
            section.push(this.showInactiveCheckboxContainer);
        return section;
    },

    getPreFooterSection: function()
    {
        return []
    },

    /**
     * @see AT.SelectorPanel
     */
    getFooterSection: function() {
        var res = [ this.closeButton ];
        if (this.showResetButton) {
            res.push(this.resetButton);
        }
        return res;
    },

    /**
     * @see AT.SelectorPanel
     */
    updateState: function() {
        if (this.isActiveActionsPermitted() && this.showShowInactiveCheckbox) {
            this.showInactiveCheckbox.setChecked(this.showInactive);
        }
        this.selectOption(this.optionValue);
    },

    /**
     * @see AT.SelectorPanel
     */
    resetState: function(isEnableActiveActions) {
        this.selectOption(this.optionValueInit);
        if (this.isActiveActionsPermitted() && isEnableActiveActions) {
           this.resetNodes();
        }
        this.updateState();
        if (isEnableActiveActions)
        {
            this.fireChangeEvent();
        }
    },

    resetNodes: function(){
        this.selectorTree.resetNodes();
    },

    isActiveActionsPermitted: function() {
        return true;
    },

    isStateChanged: function() {
        if (this.getCurrentOptionValue() !== this.optionValueInit) {
            return true;
        }
        return this.isCustomOptionSelected() && this.isSelectionChanged();
    },

    isSelectionChanged: function() {
        return this.selectorTree.isSelectionChanged();
    },

    isTruncateStrings: function() {
        return true;
    },

    /*
     Do not pass maxlength if you want to not truncate strings by it
     */
    getAllSelectedString: function(maxLength) {
        return (maxLength && this.isTruncateStrings()) ? truncateStringByLength( this.currentOption.boxLabel, maxLength, true ) : this.currentOption.boxLabel;
    },

    getDescription: function(selectionHelper, dropdownButtonId) {
        //abstract
    },

    //private
    /*
    Do not pass maxlength if you want to not truncate strings by it
     */
    getLabelTextTemplate: function(customSelectedFunction, maxLength) {
        if (this.currentOption != this.customOption) {
            return this.getAllSelectedString(maxLength);
        } else {
            return customSelectedFunction();
        }
    },

    getTooltipText: function() {
        var context = this;
        return this.getLabelTextTemplate(function() {return context.getTooltipForCustomOption()});
    },

    getLabelText: function(dropdownButtonId, selectionHelper, maxLength) {
        if (!selectionHelper) {
            selectionHelper = this.getSelectionHelper();
        }
        if (!maxLength) {
            maxLength = this.labelMaxChars;
        }
        var context = this;
        return this.getLabelTextTemplate(function() {return context.getDescription(selectionHelper, dropdownButtonId)}, maxLength);
    },

    isTooltipEnabled: function() {
        //abstract
    },

    getTooltipForCustomOption: function() {
        //abstract
    },

    /**
     * @returns the value of the "show inactive" checkbox.
     */
    getShowInactive: function() {
        return (this.isActiveActionsPermitted()) ? this.showInactiveCheckbox.isChecked() : this.showInactive;
    },

    setCurrentOptionValue: function( value ) {
        this.optionValue = value;
        this.updateState();
    },

    setInitialOptionValue: function( value ) {
        this.optionValueInit = value;
        this.setCurrentOptionValue( value );
    },

    /**
     * Sets initial value of Show Disabled Checkbox
     * @param flag value to set
     *
     * TODO: this is confusing.
     *       The comment says about 'initial value', but no 'initials' can be seen in implementation,
     *       and the method name is abstract and fuzzy.
     *       The 'showInactive' field may be treated as 'initial value', as opposed to the 'getShowInactive' method,
     *       which returns the 'current value'. But every time the 'updateState' method is called, the initial value
     *       becomes current value!
     */
    setFilterFlag: function ( flag )
    {
        this.showInactive = flag;
    },

    /**
     * Replaces stored initial selection and option values with the current ones.
     */
    setInitialValuesFromCurrent: function()
    {
        this.setFilterFlag( this.getShowInactive() );               // set initial value for 'Show inactive' checkbox
        this.setInitialOptionValue( this.getCurrentOptionValue() ); // set initial value for 'All' option
        this.selectorTree.setInitialSelectionFromCurrent();         // set
    },

    isNeedToMarkAllSelected: function() {
        return this.isMarkAllSelectedIsEmpty && this.isEmptySelection()
    },

    markAllSelectedIfNeeded: function() {
        if (this.isNeedToMarkAllSelected()) {   // if nothing selected
            this.setCurrentOptionValue(true);  // then force 'All' option to be selected
        }
    },

    isCustomOptionSelected: function() {
        return this.currentOption.inputValue == this.customOptionValue;
    },

    isEmptySelection: function() {
        //abstract
    }

});

export default GenericSelectorPanel;


// WEBPACK FOOTER //
// ./web/js/controls/selectorControls/at_generic_selector_panel.js